import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faTrash,
  faHome,
  faBuilding,
  faBed,
  faBath,
  faRulerCombined,
  faLayerGroup,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const PropertyUnitEdit = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [unit, setUnit] = useState(null);
  const [group, setGroup] = useState(null);
  const [formData, setFormData] = useState({
    unitNumber: '',
    unitType: 'apartment',
    bedrooms: '',
    bathrooms: '',
    area: '',
    floor: '',
    status: 'available',
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUnit = async () => {
      try {
        setLoading(true);
        const unitData = await propertyService.getPropertyUnitById(unitId);
        setUnit(unitData);
        
        // Fetch the property group details
        if (unitData.groupId) {
          const groupData = await propertyService.getPropertyGroupById(unitData.groupId);
          setGroup(groupData);
        }
        
        setFormData({
          unitNumber: unitData.unitNumber || '',
          unitType: unitData.unitType || 'apartment',
          bedrooms: unitData.bedrooms || '',
          bathrooms: unitData.bathrooms || '',
          area: unitData.area || '',
          floor: unitData.floor || '',
          status: unitData.status || 'available',
          description: unitData.description || ''
        });
      } catch (error) {
        console.error('Error fetching unit:', error);
        setError('Failed to load property unit: ' + (error.message || error));
      } finally {
        setLoading(false);
      }
    };

    if (unitId) {
      fetchUnit();
    }
  }, [unitId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.unitNumber.trim()) {
      setError('Please enter a unit number');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updateData = {
        unitNumber: formData.unitNumber.trim(),
        unitType: formData.unitType,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : 0,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : 0,
        area: formData.area ? parseFloat(formData.area) : 0,
        floor: formData.floor ? parseInt(formData.floor) : 0,
        status: formData.status,
        description: formData.description.trim()
      };

      await propertyService.updatePropertyUnit(unitId, updateData);

      setSuccess('Property unit updated successfully!');
      
      // Redirect back to unit details after a short delay
      setTimeout(() => {
        navigate(`/manager/units/${unitId}`);
      }, 1500);

    } catch (error) {
      console.error('Error updating unit:', error);
      setError('Failed to update property unit: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this property unit?')) {
      try {
        setSaving(true);
        await propertyService.deletePropertyUnit(unitId);
        navigate(`/manager/property-groups/${unit.groupId}`);
      } catch (error) {
        console.error('Error deleting unit:', error);
        setError('Failed to delete property unit: ' + (error.message || error));
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !unit) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          {error}
          <Button 
            variant="outline-danger" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Property unit not found
          <Button 
            variant="outline-warning" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/manager/units/${unitId}`)}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Unit
          </Button>
          <div>
            <h2 className="mb-1">Edit Property Unit</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faHome} className="me-1" />
              {unit.unitNumber} • {group?.name}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                Property Unit Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faHome} className="me-1" />
                        Unit Number <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="unitNumber"
                        value={formData.unitNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., A-101, B-205"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Type <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="unitType"
                        value={formData.unitType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="apartment">Apartment</option>
                        <option value="villa">Villa</option>
                        <option value="penthouse">Penthouse</option>
                        <option value="studio">Studio</option>
                        <option value="townhouse">Townhouse</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faBed} className="me-1" />
                        Bedrooms
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="bedrooms"
                        value={formData.bedrooms}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faBath} className="me-1" />
                        Bathrooms
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="bathrooms"
                        value={formData.bathrooms}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        step="0.5"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faRulerCombined} className="me-1" />
                        Area (sq ft)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                        Floor
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="floor"
                        value={formData.floor}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="reserved">Reserved</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter unit description..."
                    rows="3"
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline-secondary"
                    onClick={() => navigate(`/manager/units/${unitId}`)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete Unit
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PropertyUnitEdit; 